import { useTranslation } from 'react-i18next';
import type {
  CenteredLoaderProps,
  InlineLoaderProps,
  LoadingButtonProps,
  LoadingOverlayProps,
  PageLoaderProps,
} from '../types/ui';

export const PageLoader = ({ message, height = '400px' }: PageLoaderProps) => {
  const { t } = useTranslation();

  return (
    <div className="container-xl">
      <div className="d-flex flex-column justify-content-center align-items-center" style={{ height }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{t('common.loading')}</span>
        </div>
        {message && <p className="mt-3 text-muted">{message}</p>}
      </div>
    </div>
  );
};

export const CenteredLoader = ({ message, className = 'my-5' }: CenteredLoaderProps) => {
  const { t } = useTranslation();

  return (
    <div className={`text-center ${className}`.trim()}>
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">{t('common.loading')}</span>
      </div>
      {message && <p className="mt-3 text-muted">{message}</p>}
    </div>
  );
};

export const InlineLoader = ({ size = 'sm', className = '' }: InlineLoaderProps) => {
  const { t } = useTranslation();

  return (
    <span className={`spinner-border spinner-border-${size} ${className}`.trim()} role="status" aria-hidden="true">
      <span className="visually-hidden">{t('common.loading')}</span>
    </span>
  );
};

export const LoadingOverlay = ({ show, message }: LoadingOverlayProps) => {
  const { t } = useTranslation();

  if (!show) {
    return null;
  }

  return (
    <div
      className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center bg-white bg-opacity-75"
      style={{ zIndex: 1000 }}
    >
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">{t('common.loading')}</span>
      </div>
      {message && <p className="mt-3 text-muted">{message}</p>}
    </div>
  );
};

export const LoadingButton = ({
  loading = false,
  disabled,
  children,
  loadingText,
  className = 'btn btn-primary',
  ...buttonProps
}: LoadingButtonProps) => {
  const { t } = useTranslation();

  return (
    <button className={className} disabled={disabled || loading} {...buttonProps}>
      {loading ? (
        <>
          <InlineLoader className="me-2" />
          {loadingText || t('common.loading')}
        </>
      ) : (
        children
      )}
    </button>
  );
};

export default PageLoader;
